import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  TenantHome,
  TenantOverview,
  TenantPage,
} from '../../../api/tenant-client';
import {
  addDays,
  clockDayTime,
  clockNote,
  clockTime,
  dayKeyIn,
  resolveClock,
  todayIn,
  zoneLabel,
} from '../../../function-utils/building-time';
import { countOf } from '../../../function-utils/plural';
import { PASS_ERROR_COPY } from '../../../utils/pass-errors';
import { AppButton } from '../../button/app-button';
import { LoadError } from '../dashboard/dashboard-shared-components/load-state';
import { ListFooter, PageNote } from './portal-kit.styled';

/**
 * WHAT EVERY PORTAL TAB SHARES: how a panel loads, how a list pages, and what
 * time it is at the resident's building.
 *
 * ── ONE FAILING PANEL IS A GAP, NOT A BLANK PAGE ──
 * The single-page portal loaded its three reads with Promise.allSettled so a
 * failed parcel list could not take the resident's home and visitors with it,
 * and it named what could not be read ("your parcels") rather than printing an
 * empty list as if it were an answer. The three reads now live on three tabs,
 * each panel loading on its own, so that independence is structural — and each
 * still names itself when it fails.
 *
 * ── EVERY PANEL CAN BE RE-READ ──
 * `reload()` on each hook re-reads the panel in place (Staleable dims it; no
 * skeleton on a refetch). The tabs call it when the resident comes back to the
 * page, and the live refresh (usePortalLiveRefresh) calls the same function
 * when the bell hears of a visitor or a parcel.
 *
 * ── A FAILED READ SAYS WHY, WHEN IT KNOWS (RP-9.6, RP-9.9) ──
 * Each hook keeps the status of its latest failed read. Two of them mean
 * something a retry will not fix:
 *   429  the reader is rate-limited — said as that (readFailure), never as
 *        "could not load";
 *   403  every portal read is refused — the reader's last home has gone. The
 *        shell asks the session ONCE what it may still do (PortalAccessContext),
 *        and the route guard then shows the "no home" screen instead of three
 *        panels of "Try again".
 */

/** The first page, and every "Show more" after it. The server clamps 1–100. */
export const PAGE_SIZE = 40;

/* ────────────────────────────  Live refresh  ──────────────────────────── */

/* WHICH NOTIFICATIONS CHANGE WHICH PANEL, by event-type prefix. A parcel
   frame has nothing to say to the visitors list and the other way round; the
   overview (the Home headline counts both) listens to all of them. */
export const LIVE_VISITOR_EVENTS = ['visitor.', 'visitor_pass.'];
export const LIVE_PARCEL_EVENTS = ['package.'];
export const LIVE_PORTAL_EVENTS = [...LIVE_VISITOR_EVENTS, ...LIVE_PARCEL_EVENTS];

export type PanelStatus = 'loading' | 'error' | 'ready';

/* ───────────────────────────  Why a read failed  ──────────────────────── */

const statusOf = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;

  return typeof status === 'number' ? status : null;
};

/**
 * What a panel says when its read failed: the rate limit in its own words,
 * otherwise the panel's own sentence ("We could not load your parcels just
 * now.").
 */
export const readFailure = (status: number | null | undefined, plain: string): string =>
  status === 429 ? PASS_ERROR_COPY.tooManyReads : plain;

/**
 * Called when a portal read answers 403. The shell provides it: one /auth
 * refresh per visit to the portal, after which the route guard knows whether
 * the reader still has a home. Outside the shell it does nothing.
 */
export const PortalAccessContext = createContext<() => void>(() => undefined);

/* ─────────────────────────────  Re-reading  ───────────────────────────── */

/**
 * RE-READ WHEN THE RESIDENT COMES BACK. A phone left on this screen used to
 * keep saying "You have 2 parcels waiting" long after they were collected.
 * Focus/visibility rather than a timer: a screen that is off is not a reader.
 * Skipped while a read is already running — returning to a tab fires both
 * events, and one re-read answers both.
 */
const useRereadOnReturn = (
  reread: () => void,
  busy: React.MutableRefObject<boolean>
) => {
  const latest = useRef(reread);
  latest.current = reread;

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState === 'visible' && !busy.current) {
        latest.current();
      }
    };

    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);

    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [busy]);
};

/* ──────────────────────────────  One read  ────────────────────────────── */

export interface Panel<T> {
  data: T | null;
  /** First load only; a refetch keeps 'ready' and dims instead. */
  status: PanelStatus;
  /** The LATEST read failed. With data on screen that data is stale, and the
   *  panel says so above it rather than presenting it as current. */
  failed: boolean;
  /** The HTTP status of that failed read, or null (no answer, or it worked). */
  errorStatus: number | null;
  refreshing: boolean;
  reload: () => void;
}

export const usePanel = <T,>(
  load: () => Promise<T>,
  /** The shell's own panel sits ABOVE the provider, so it is handed this. */
  onForbidden?: () => void
): Panel<T> => {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<PanelStatus>('loading');
  const [failed, setFailed] = useState(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const seq = useRef(0);
  const busy = useRef(false);
  const alive = useRef(true);
  const fromShell = useContext(PortalAccessContext);
  const forbidden = useRef(onForbidden || fromShell);
  forbidden.current = onForbidden || fromShell;

  const reload = useCallback(() => {
    const mine = ++seq.current;
    busy.current = true;
    setRefreshing(true);

    load().then(
      value => {
        if (!alive.current || mine !== seq.current) return;
        setData(value);
        setFailed(false);
        setErrorStatus(null);
        setStatus('ready');
      },
      error => {
        if (!alive.current || mine !== seq.current) return;
        setFailed(true);
        setErrorStatus(statusOf(error));
        setStatus(current => (current === 'ready' ? 'ready' : 'error'));
        if (statusOf(error) === 403) forbidden.current();
      }
    ).finally(() => {
      if (!alive.current || mine !== seq.current) return;
      busy.current = false;
      setRefreshing(false);
    });
  }, [load]);

  useEffect(() => {
    alive.current = true;
    reload();

    return () => {
      alive.current = false;
    };
  }, [reload]);

  useRereadOnReturn(reload, busy);

  return { data, status, failed, errorStatus, refreshing, reload };
};

/* ─────────────────────────────  A paged list  ─────────────────────────── */

export interface PagedPanel<T> {
  items: T[];
  total: number;
  nextCursor: string | null;
  /** How many pages are on screen — 1 until "Show more" is pressed. */
  pages: number;
  status: PanelStatus;
  failed: boolean;
  /** The HTTP status of the latest failed first-page read, or null. */
  errorStatus: number | null;
  refreshing: boolean;
  loadingMore: boolean;
  /** The last "Show more" failed; the rows already shown stay. */
  moreFailed: boolean;
  /** Re-read from the first page. Rows paged in after it are dropped rather
   *  than merged: a merge cannot tell a parcel that moved from waiting to
   *  collected from one that is new, and a list that is honest about its
   *  first page beats one that is confidently wrong about its fourth. */
  reload: () => void;
  /** The next page, with before=<next_cursor>. No-op at the end. */
  loadMore: () => void;
}

type PageLoader<T> = (opts: {
  limit?: number;
  before?: string | null;
}) => Promise<TenantPage<T>>;

/* A row can arrive twice across pages — a parcel collected between two reads
   is on page 1 as waiting and on a later page as collected. The first copy
   wins; two rows with one key is a React warning and a doubled parcel. */
const appendUnique = <T extends { id: string | number }>(
  current: T[],
  next: T[]
): T[] => {
  const seen = new Set(current.map(item => item.id));

  return [...current, ...next.filter(item => !seen.has(item.id))];
};

export const usePagedPanel = <T extends { id: string | number }>(
  loadPage: PageLoader<T>
): PagedPanel<T> => {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState<PanelStatus>('loading');
  const [failed, setFailed] = useState(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const fromShell = useContext(PortalAccessContext);
  const forbidden = useRef(fromShell);
  forbidden.current = fromShell;
  const [moreFailed, setMoreFailed] = useState(false);
  /* One generation per first-page read. A "Show more" that answers after a
     re-read started belongs to a list that no longer exists, and is dropped. */
  const generation = useRef(0);
  const cursor = useRef<string | null>(null);
  const busy = useRef(false);
  const moreInFlight = useRef(false);
  const alive = useRef(true);

  const reload = useCallback(() => {
    const mine = ++generation.current;
    busy.current = true;
    moreInFlight.current = false;
    setLoadingMore(false);
    setMoreFailed(false);
    setRefreshing(true);

    loadPage({ limit: PAGE_SIZE }).then(
      page => {
        if (!alive.current || mine !== generation.current) return;
        cursor.current = page.next_cursor;
        setItems(appendUnique([], page.items));
        setTotal(page.total);
        setNextCursor(page.next_cursor);
        setPages(1);
        setFailed(false);
        setErrorStatus(null);
        setStatus('ready');
      },
      error => {
        if (!alive.current || mine !== generation.current) return;
        setFailed(true);
        setErrorStatus(statusOf(error));
        setStatus(current => (current === 'ready' ? 'ready' : 'error'));
        if (statusOf(error) === 403) forbidden.current();
      }
    ).finally(() => {
      if (!alive.current || mine !== generation.current) return;
      busy.current = false;
      setRefreshing(false);
    });
  }, [loadPage]);

  const loadMore = useCallback(() => {
    const before = cursor.current;
    if (!before || moreInFlight.current) return;
    const mine = generation.current;
    moreInFlight.current = true;
    setLoadingMore(true);
    setMoreFailed(false);

    loadPage({ limit: PAGE_SIZE, before }).then(
      page => {
        if (!alive.current || mine !== generation.current) return;
        cursor.current = page.next_cursor;
        setItems(current => appendUnique(current, page.items));
        setTotal(page.total);
        setNextCursor(page.next_cursor);
        setPages(count => count + 1);
      },
      () => {
        if (!alive.current || mine !== generation.current) return;
        setMoreFailed(true);
      }
    ).finally(() => {
      if (!alive.current || mine !== generation.current) return;
      moreInFlight.current = false;
      setLoadingMore(false);
    });
  }, [loadPage]);

  useEffect(() => {
    alive.current = true;
    reload();

    return () => {
      alive.current = false;
    };
  }, [reload]);

  useRereadOnReturn(reload, busy);

  return {
    items,
    total,
    nextCursor,
    pages,
    status,
    failed,
    errorStatus,
    refreshing,
    loadingMore,
    moreFailed,
    reload,
    loadMore,
  };
};

/* ─────────────────────────  The overview, shared  ─────────────────────── */

/**
 * The overview is read ONCE, by the shell, and shared: Home renders it and the
 * two list tabs read the building's time zone from it. Reading it per tab would
 * fetch it again on every tab press.
 */
export const PortalOverviewContext = createContext<Panel<TenantOverview> | null>(
  null
);

export const usePortalOverview = (): Panel<TenantOverview> => {
  const panel = useContext(PortalOverviewContext);
  if (!panel) {
    throw new Error('usePortalOverview must be used inside the portal shell');
  }

  return panel;
};

/** The primary home: the server's `home`, else the first of `homes`. */
export const primaryHome = (
  overview: TenantOverview | null
): TenantHome | null => overview?.home ?? overview?.homes?.[0] ?? null;

/**
 * THE ZONE A LIST ROW IS PRINTED IN (RP-9.9): its own building's, which the
 * server sends on each visit and parcel. A resident with two homes in two
 * zones used to read every row in the first home's clock. A row without one
 * (an older server) falls back to the primary home's, as before.
 */
export const rowZone = (
  row: { building_timezone?: string | null },
  fallback?: string
): string | undefined => row.building_timezone || fallback;

/**
 * The zones a list's rows are in, for the note under it. One zone reads as it
 * always has ("Times in Europe/Sofia"); two or more cannot be covered by one
 * sentence, so each row names its own (zoneTag) and the list-wide note goes.
 */
export const zonesOf = (
  rows: ReadonlyArray<{ building_timezone?: string | null }>,
  fallback?: string
): string[] => Array.from(new Set(rows.map(row => rowZone(row, fallback) || '').filter(Boolean)));

/* ──────────────────────────  Times, in the building  ──────────────────── */

/**
 * "Today, 14:05" / "Yesterday, 09:12" / "12 Sept, 18:40" — in the BUILDING's
 * zone. The single-page portal formatted in the phone's own zone, so a resident
 * travelling abroad was told their parcel arrived at a time the desk never saw.
 * Day keys and their arithmetic come from the one time module.
 */
export const whenAt = (iso: string | null | undefined, tz?: string): string => {
  if (!iso) return '';
  const day = dayKeyIn(iso, tz);
  if (!day) return '';

  const today = todayIn(tz);
  if (day === today) return `Today, ${clockTime(iso, tz)}`;
  if (day === addDays(today, -1)) return `Yesterday, ${clockTime(iso, tz)}`;

  return clockDayTime(iso, tz);
};

/**
 * WHICH CLOCK THE PORTAL'S TIMES ARE ON — always said (R-T12: no time is shown
 * with the reader left to guess). The portal is a building-clock surface, so
 * this reads "Building time — Toronto" for whoever is in it; for a building
 * that has no usable zone yet it says the times are the device's instead of
 * passing them off as the building's.
 */
export const zoneNote = (tz?: string): string =>
  clockNote(resolveClock({ surface: 'portal', buildingZone: tz }));

/**
 * " · Sofia time" after a row's time — only when the list mixes zones, so the
 * reader can tell which clock each row is in. '' otherwise.
 */
export const zoneTag = (tz: string | undefined, mixed: boolean): string => {
  if (!mixed) return '';
  const zone = zoneLabel(tz);

  return zone ? ` · ${zone} time` : '';
};

/* ─────────────────────────────  The list footer  ──────────────────────── */

interface ListMoreProps {
  shown: number;
  total: number;
  nextCursor: string | null;
  pages: number;
  loadingMore: boolean;
  moreFailed: boolean;
  onMore: () => void;
  /** "visit" / "parcel" — for the end-of-list sentence. */
  noun: string;
  /** What a failed page was of, in the reader's words: "visits". */
  what: string;
  /** zoneNote() for the list's times, or ''. */
  zone?: string;
}

/**
 * "Show more" and how far through the list you are. The note stays when the
 * button goes, so the end of a list is stated rather than implied — a button
 * that simply stops appearing reads the same as one that broke.
 *
 * The paging note appears only once there is paging to speak of: a resident
 * with three visits does not need to be told they are looking at all three.
 * The clock the times are on is always said (zoneNote).
 */
export const ListMore: React.FC<ListMoreProps> = ({
  shown,
  total,
  nextCursor,
  pages,
  loadingMore,
  moreFailed,
  onMore,
  noun,
  what,
  zone = '',
}) => {
  const note = useRef<HTMLParagraphElement>(null);
  const hadMore = useRef(Boolean(nextCursor));

  /* THE BUTTON THAT WAS PRESSED IS GONE once the last page lands, and focus
     would fall to the top of the document. It moves to the sentence that says
     why instead. */
  useEffect(() => {
    if (hadMore.current && !nextCursor && pages > 1) {
      note.current?.focus();
    }
    hadMore.current = Boolean(nextCursor);
  }, [nextCursor, pages]);

  const paged = Boolean(nextCursor) || pages > 1;
  if (!paged && !(zone && shown)) return null;

  return (
    <>
      {moreFailed ? (
        <LoadError message={`We could not load more ${what} just now.`} onRetry={onMore} />
      ) : null}
      <ListFooter>
        {nextCursor ? (
          <AppButton
            variant="ghost"
            onClick={onMore}
            loading={loadingMore}
            loadingLabel="Loading more"
          >
            Show more
          </AppButton>
        ) : null}
        {paged ? (
          <PageNote ref={note} tabIndex={-1} role="status">
            {nextCursor
              ? `Showing ${shown} of ${Math.max(total, shown)}`
              : `All ${countOf(shown, noun)} shown`}
          </PageNote>
        ) : null}
        {zone && shown ? (
          <PageNote data-clock-note="" data-clock-whose="building">
            {zone}
          </PageNote>
        ) : null}
      </ListFooter>
    </>
  );
};
